import {Component, Inject, LOCALE_ID, OnDestroy, OnInit} from '@angular/core';
import {NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterEvent} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {fromEvent} from 'rxjs';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {ModalService} from '~shared/components/elements/global-modal/modal.service';
import {NotificationType} from '~core/enum';
import {environment} from '~environments/environment';
import {
    CountryService,
    CustomerService,
    DefaultNotificationService,
    ScreenSizeService,
    StorageService,
    TixxConnectionService,
} from 'src/app/core/services';
import {SharedTranslationsService} from 'src/app/shared/translations';

declare let gtag: (arg0: string, arg1: string, arg2: { page_path: string; }) => void;

/**
 * Initialize the main app component.
 */
@UntilDestroy()
@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss']
})

export class AppComponent implements OnInit, OnDestroy {
    public spinnerName: string = 'screenSpinner';
    public loading: boolean = true;
    public isTixxOpened: boolean = false;

    constructor(
        public router: Router,
        public userService: CustomerService,
        public tixxConnectionService: TixxConnectionService,
        @Inject(LOCALE_ID) public locale: string,
        private screenSizeService: ScreenSizeService,
        private storage: StorageService,
        private countryService: CountryService,
        private notificationService: DefaultNotificationService,
        private sharedTranslations: SharedTranslationsService,
        public spinnerService: SpinnerService,
        public modalService: ModalService
    ) {
        router.events.subscribe((routerEvent: any) => {
            this.checkRouterEvent(routerEvent);
        });
    }

    public ngOnDestroy(): void {
    }

    /**
     * After initialization load the currently logged in user.
     */
    public ngOnInit(): any {
        // Redirect to maintenance page if maintenanceMode enabled
        if (environment.maintenanceMode as any === true) {
            this.router.navigate(['/maintenance']);
        }
        this.screenSizeService.setScreenSize(window.innerWidth);
        fromEvent(window, 'resize')
            .subscribe((e: any) => {
                this.screenSizeService.setScreenSize(e.target.innerWidth);
            });

        this.tixxConnectionService.getTixxConnectionEstablished().pipe(untilDestroyed(this))
            .subscribe((data: boolean) => {
                if (data) {
                    document.body.style.overflow = 'hidden';
                    this.isTixxOpened = true;
                }
            });

        if (this.storage.isLoggedIn()) {
            if (!this.storage.hasCountries()) {
                this.countryService.getCountries()
                    .pipe(untilDestroyed(this))
                    .subscribe({
                        next: (fetchedCountries => {
                            this.storage.setCurrentCountries(fetchedCountries, `${this.locale}-countries`);
                            this.storage.loadCurrentCountries(`${this.locale}-countries`);
                        }),
                        error: (e => {
                            this.notificationService.showNotification({
                                type: NotificationType.SUCCESS,
                                message: this.sharedTranslations.i18nErrorFetchingCountries
                            });
                        })
                    });
            } else {
                this.storage.loadCurrentCountries(`${this.locale}-countries`);
            }
        }
    }

    /**
     * Executes loading on start of changing routes
     */
    private checkRouterEvent(routerEvent: RouterEvent): any {
        if (routerEvent instanceof NavigationStart) {
            this.spinnerService.show(this.spinnerName);
            if (document.getElementsByTagName('eds-tooltip')?.length > 0) {
                document.getElementsByTagName('eds-tooltip')[0].remove();
            }
        }

        if (routerEvent instanceof NavigationEnd ||
            routerEvent instanceof NavigationCancel ||
            routerEvent instanceof NavigationError) {
            this.spinnerService.hide(this.spinnerName);
            // this.loadEdsJs();
        }

        if (routerEvent instanceof NavigationEnd && gtag) {
            gtag('config', environment.analyticsKey, {
                page_path: `${routerEvent.urlAfterRedirects}`
            });

        }
    }

    /**
     * Close the tix connection overlay.
     */
    public closeTixxConnection(): void {
        this.isTixxOpened = false;
        document.body.style.overflow = '';
    }
}
